import _ from 'lodash';

import { CONSTRUCTION_BUDGET_CATEGORY_LABEL } from './types';

const PERCENT_SCALE = 100;

// The Interfirst.Skynet.Loans.API construction endpoints return the raw domain DTO
// (totalBudgetAmount / lineItems[].budgetedAmount / pendingDrawTotal / remainingToDraw …), while the
// construction UI selectors + components consume a normalized view model (totalBudget / lines[].costCode
// / budgeted / thisDraw / remaining …). These mappers are the adapter between the two shapes — without
// them the store holds the raw DTO and every selector reads an absent field, so the budget + draws render
// empty. Keep the raw fields on the object too, so anything reading the DTO field name still works.

const mapBudgetLine = line => {
  const budgeted = line.budgetedAmount ?? 0;
  const fundedToDate = line.fundedToDate ?? 0;
  const thisDraw = line.pendingDrawTotal ?? 0;
  const remaining = line.remainingToDraw ?? budgeted - fundedToDate - thisDraw;

  return {
    ...line,
    costCode: line.costCode ?? CONSTRUCTION_BUDGET_CATEGORY_LABEL[line.category] ?? line.category,
    description: line.description,
    budgeted,
    fundedToDate,
    thisDraw,
    remaining,
    percentComplete: budgeted ? Math.round((fundedToDate / budgeted) * PERCENT_SCALE) : 0,
    isOverBudget: remaining < 0,
  };
};

export const mapConstructionBudget = data => {
  // A no-budget loan returns 204 No Content, which axios surfaces as an empty-string body ('') — not
  // null. Guard on "is it an object" rather than isNil so the empty-string 204 also maps to null;
  // otherwise the budget selector holds a truthy empty object and the tab renders EDIT (dead-end)
  // instead of the CREATE path.
  if (!_.isObject(data)) {
    return null;
  }

  return {
    ...data,
    totalBudget: data.totalBudgetAmount ?? 0,
    fundedToDate: data.fundedToDate ?? 0,
    remaining: data.availableToDraw ?? 0,
    retainageHeld: data.retainageHeld ?? 0,
    contingencyRemaining: data.contingencyAmount ?? 0,
    lines: (data.lineItems ?? []).map(mapBudgetLine),
  };
};

const mapDrawLineItem = line => ({
  ...line,
  requested: line.requestedAmount ?? 0,
  approved: line.approvedAmount ?? null,
  percentComplete: line.percentComplete ?? 0,
});

export const mapDrawRequest = draw => {
  if (_.isNil(draw)) {
    return null;
  }

  const lineItems = (draw.lineItems ?? []).map(mapDrawLineItem);
  const hasApproved = lineItems.some(line => !_.isNil(line.approvedAmount));
  const requested = draw.totalRequestedAmount ?? 0;

  return {
    ...draw,
    requested,
    approved: hasApproved ? _.sumBy(lineItems, line => line.approvedAmount ?? 0) : null,
    retainageHeld: requested * ((draw.retainagePercent ?? 0) / PERCENT_SCALE),
    lineItems,
  };
};

// A draw's funded contribution is its approved total once it reaches Approved/Disbursed (the amount
// that has been added to the budget's fundedToDate); a still-in-review draw has funded 0.
const getDrawFundedAmount = draw => draw.totalApprovedAmount ?? draw.approved ?? 0;

// "Prior Draws Funded" for a draw is the sum of the funded amounts of every OTHER draw that came before
// it chronologically. Nothing on the per-draw API DTO carries this, so it is derived here across the
// full list (the review drawer only reads drawRequest.priorDrawsFunded) — DEF-5. Draws are ranked by
// request/created date to establish "before"; the result is returned in the original list order.
export const mapDrawRequests = (data = []) => {
  const draws = (data ?? []).map(mapDrawRequest);
  const chronological = _.orderBy(
    draws,
    [draw => draw.requestDate ?? draw.createdDate ?? ''],
    ['asc'],
  );

  const priorByDrawId = chronological.reduce(
    (accumulator, draw) => {
      accumulator.byId[draw.id] = accumulator.runningTotal;
      accumulator.runningTotal += getDrawFundedAmount(draw);

      return accumulator;
    },
    { byId: {}, runningTotal: 0 },
  ).byId;

  return draws.map(draw => ({ ...draw, priorDrawsFunded: priorByDrawId[draw.id] ?? 0 }));
};
